import { FluentBundle } from "fluent";
import { GetString } from "fluent-react";
import sortBy from "lodash-es/sortBy";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { semiBoldFontWeight } from "../../cssVariables";
import {
  CountryLookback,
  CountryYear,
  GlobalDatum,
  Location,
  LocationLevel,
  LookBackYearRange,
  ProductClass,
  TreeMapInput,
  TreeMapType,
  QueryLevel,
} from "../../graphQL/types/shared";
import { gql, useQuery } from "../../graphQL/useQuery";
import {
  formatPercentage,
  formatTradeValueFullWord,
} from "../../numberFormatters";
import useOutsideClick from "../../sharedComponents/useOutsideClick";
import { FetchedLocationDatum } from "../../tree/newChart/graphQLTypes";
import { getCountryName } from "../../Utils";
import { smallMediaQuery } from "../Grid";
import Localized from "../landing/Localized";
import { numberToSign, primaryColor1, secondaryColor3 } from "../Utils";
import { ContentContainer, IntroText, Title } from "./SharedStyling";
import Slide from "./Slide";
import SlideAdvancer, { Direction } from "./SlideAdvancer";

const textColor = "#666";

const TradeDirectionToggleContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const TradeDirectionToggle = styled.div`
  pointer-events: auto;
  border-bottom: solid 2px ${primaryColor1};
  padding-bottom: 2px;
  cursor: pointer;
  color: ${textColor};

  &:hover {
    color: ${primaryColor1};
  }
`;

const TradeDirectionMenu = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  transform: translateY(50%);
  border-top: 1px solid ${secondaryColor3};
  border-left: 1px solid ${secondaryColor3};
  border-right: 1px solid ${secondaryColor3};
`;

const TradeDirectionMenuItem = styled.div`
  pointer-events: auto;
  cursor: pointer;
  color: ${primaryColor1};
  border-bottom: 1px solid ${secondaryColor3};
  border-top: none;
  font-size: 1rem;
  padding: 0.3rem 0.8rem;
  background-color: #fff;
  white-space: nowrap;

  &:hover {
    background-color: #ebedf0;
  }

  &:focus,
  &:active {
    outline: none;
  }

  @media ${smallMediaQuery} {
    font-size: 0.7rem;
  }
`;

const TradeDirectionTitle = styled.div`
  font-size: 1.2rem;
  text-align: center;
  color: ${textColor};
  margin-bottom: 0.8rem;

  @media ${smallMediaQuery} {
    font-size: 0.9rem;
    margin-bottom: 0.65rem;
  }
`;

const ArrowOuterContainer = styled.div`
  right: 0.625rem;
  width: 0.75rem;
  display: inline-block;
  align-items: center;
  margin: 0 0.2rem;

  @media ${smallMediaQuery} {
    width: 0.5rem;
  }
`;
const ArrowInnerContainer = styled.div`
  width: 100%;
  height: 0;
  position: relative;
`;
const Polyline = styled.polyline`
  stroke: ${primaryColor1};
`;

const Arrow = () => (
  <ArrowOuterContainer>
    <ArrowInnerContainer>
      <svg viewBox="0 0 9.25 5.39">
        <Polyline
          strokeWidth="1.2"
          fill="none"
          points="8.88 0.38 4.64 4.62 0.39 0.38"
        />
      </svg>
    </ArrowInnerContainer>
  </ArrowOuterContainer>
);

const TopTradeDestinationCountriesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  margin-bottom: 1.7rem;
`;

const TopTradeCountry = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const CountryPercentage = styled.div`
  font-size: 1.6rem;
  color: ${primaryColor1};
  font-weight: ${semiBoldFontWeight};
  margin-bottom: 0.5rem;

  @media ${smallMediaQuery} {
    font-size: 0.9rem;
    margin-bottom: 0.4rem;
  }
`;

const CountryName = styled.div`
  font-size: 1.1rem;
  color: ${textColor};
  line-height: 1.3;
  text-align: center;

  @media ${smallMediaQuery} {
    font-size: 0.9rem;
  }
`;

const query = gql`
  query ExportBasketSideBar(
    $location: ID!
    $year: Int!
    $yearRange: LookBackYearRange!
    $facet: TreeMapType!
    $productClass: ProductClass
    $productLevel: ProductLevel
    $locationLevel: LocationLevel
    $product: ID
    $partner: ID
  ) {
    location(id: $location) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryYear(location: $location, year: $year) {
      exportValue
      importValue
    }
    countryLookback(id: $location, yearRange: $yearRange) {
      exportValueGrowthCagr: exportValueConstGrowthCagr
      exportValueGrowth: exportValueConstGrowthCagr
      exportValuePercentChange: exportValueConstGrowthCagr
      exportValueGrowthNonOilCagr: exportValueGrowthNonOilConstCagr
      gdpChangeConstantCagr
    }
    globalData: globalDatum(yearRange: $yearRange) {
      globalExportValueChangeCAGR: exportValueConstChangeCagr
      globalExportValueChangeNonOilCAGR: exportValueNonOilConstChangeCagr
    }
    chart: treeMap(
      facet: $facet
      productClass: $productClass
      year: $year
      productLevel: $productLevel
      locationLevel: $locationLevel
      location: $location
      product: $product
      partner: $partner
    ) {
      ... on TreeMapLocation {
        location {
          id
          shortName
          nameAbbr
          thePrefix
        }
        exportValue
        importValue
      }
    }
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location["id"];
    shortName: Location["shortName"];
    nameAbbr: Location["nameAbbr"];
    thePrefix: Location["thePrefix"];
  };
  countryYear: null | {
    exportValue: CountryYear["exportValue"];
    importValue: CountryYear["importValue"];
  };
  countryLookback: null | {
    exportValueGrowthCagr: CountryLookback["exportValueConstGrowthCagr"];
    exportValueGrowth: CountryLookback["exportValueConstGrowthCagr"];
    exportValuePercentChange: CountryLookback["exportValueConstGrowthCagr"];
    exportValueGrowthNonOilCagr: CountryLookback["exportValueGrowthNonOilConstCagr"];
    gdpChangeConstantCagr: CountryLookback["gdpChangeConstantCagr"];
  };
  globalData: {
    globalExportValueChangeCAGR: GlobalDatum["exportValueConstChangeCagr"];
    globalExportValueChangeNonOilCAGR: GlobalDatum["exportValueNonOilConstChangeCagr"];
  };
  chart: FetchedLocationDatum[];
}

interface Variables extends TreeMapInput {
  yearRange: LookBackYearRange;
  queryLevel: QueryLevel;
}

enum TradeDirection {
  Import = "importValue",
  Export = "exportValue",
}

interface TopCountry {
  id: string;
  countryName: string;
  percentage: string;
}

const getTopDestinationCountries = (
  countries: FetchedLocationDatum[],
  direction: TradeDirection
) => {
  let totalValue = 0;
  const sortedCountries = sortBy(countries, (country) => {
    totalValue = totalValue + country[direction];
    return country[direction];
  });
  const topCountries: TopCountry[] = [];
  for (
    let i = sortedCountries.length - 1;
    i > sortedCountries.length - 4;
    i--
  ) {
    const {
      location: { id, shortName, nameAbbr, thePrefix },
    } = sortedCountries[i];
    const tradeValue = sortedCountries[i][direction];
    const { nameInChart } = getCountryName({
      shortName,
      nameAbbr,
      thePrefix: Boolean(thePrefix),
    });
    topCountries.push({
      id,
      countryName: nameInChart,
      percentage: formatPercentage(tradeValue / totalValue),
    });
  }
  return topCountries;
};

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  countryId: string;
  year: number;
  isHelpActive: boolean;
  helpTooltipIndex: number;
  fluentBundle: FluentBundle;
}

export default (props: Props) => {
  const {
    getFluentString,
    onNextSlideClick,
    onPrevSlideClick,
    countryId,
    year,
    isHelpActive,
    helpTooltipIndex,
    fluentBundle,
  } = props;

  const importExportDropdownRef = useRef(null);
  const [
    topTradeDestinationDirection,
    setTopTradeDestinationDirection,
  ] = useState<TradeDirection>(TradeDirection.Export);
  const [
    tradeDirectionDropdownIsShown,
    setTradeDirectionDropdownIsShown,
  ] = useState<boolean>(false);

  const selectTradeDirection = (
    e: React.MouseEvent,
    direction: TradeDirection
  ) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent.stopImmediatePropagation();
    setTopTradeDestinationDirection(direction);
    setTradeDirectionDropdownIsShown(false);
  };

  const toggleTradeDirectionDropdown = (e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    setTradeDirectionDropdownIsShown(!tradeDirectionDropdownIsShown);
  };

  useOutsideClick(importExportDropdownRef, () => {
    setTradeDirectionDropdownIsShown(false);
  });

  const yearAsString = year.toString();

  const variables: Variables = {
    yearRange: LookBackYearRange.FiveYears,
    facet: TreeMapType.CCY_C,
    productClass: ProductClass.HS,
    year,
    productLevel: null,
    locationLevel: LocationLevel.country,
    location: countryId,
    product: null,
    partner: null,
    queryLevel: QueryLevel.Location,
  };

  const { loading, error, data } = useQuery<SuccessResponse, Variables>(query, {
    variables,
  });
  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = null;
  } else if (error !== undefined) {
    console.error(error);
    out = null;
  } else if (data !== undefined) {
    const {
      location,
      globalData: {
        globalExportValueChangeCAGR,
        globalExportValueChangeNonOilCAGR,
      },
      chart,
      countryLookback,
      countryYear,
    } = data;
    if (location !== null && countryLookback !== null && countryYear !== null) {
      const { shortName, nameAbbr, thePrefix } = location;
      const {
        exportValueGrowthCagr,
        gdpChangeConstantCagr,
        exportValueGrowthNonOilCagr,
      } = countryLookback;
      const { exportValue, importValue } = countryYear;
      const { namePrimary, nameSecondary } = getCountryName({
        shortName,
        nameAbbr,
        thePrefix: Boolean(thePrefix),
      });
      const topTradeDestinationCountries = getTopDestinationCountries(
        chart,
        topTradeDestinationDirection
      ).map(({ id, percentage, countryName }) => (
        <TopTradeCountry key={"topTradeDestinationCountries" + id}>
          <CountryPercentage>{percentage}</CountryPercentage>
          <CountryName>{countryName}</CountryName>
        </TopTradeCountry>
      ));
      const totalExportsAsMonetaryValue =
        exportValue !== null
          ? formatTradeValueFullWord(exportValue, getFluentString)
          : "N/A";
      const tradeBalance =
        exportValue !== null && importValue !== null
          ? numberToSign(exportValue - importValue)
          : "N/A";
      const exportValueAsPercent = formatPercentage(
        Math.abs(exportValueGrowthCagr),
        1
      );
      const nonOilExportValueAsPercent = formatPercentage(
        Math.abs(exportValueGrowthNonOilCagr),
        1
      );
      const formattedImportValue =
        importValue !== null
          ? formatTradeValueFullWord(importValue, getFluentString)
          : "N/A";
      const exportGrowthComparedToEconomicGrowth =
        gdpChangeConstantCagr !== null
          ? numberToSign(exportValueGrowthCagr - gdpChangeConstantCagr)
          : "N/A";

      const introtext = getFluentString("export-basket-intro-text", {
        "country-name-first": namePrimary,
        "country-name-other": nameSecondary,
        "total-exports-as-monetary-value": totalExportsAsMonetaryValue,
        year: yearAsString,
        "export-change-as-sign": numberToSign(exportValueGrowthCagr),
        "export-change-as-percentage": exportValueAsPercent,
        "export-change-compared-to-global-average-change": numberToSign(
          exportValueGrowthCagr - globalExportValueChangeCAGR
        ),
        "non-oil-export-change-as-sign": numberToSign(
          exportValueGrowthNonOilCagr
        ),
        "non-oil-export-same-as-export":
          nonOilExportValueAsPercent === exportValueAsPercent
            ? "true"
            : "false",
        "non-oil-export-change-as-percentage": nonOilExportValueAsPercent,
        "non-oil-export-change-compared-to-global-average-change": numberToSign(
          exportValueGrowthNonOilCagr - globalExportValueChangeNonOilCAGR
        ),
        "global-average-growth-as-percentage": formatPercentage(
          globalExportValueChangeCAGR,
          1
        ),
        "export-growth-compared-to-economic-growth": exportGrowthComparedToEconomicGrowth,
        "total-imports-as-monetary-value": formattedImportValue,
        "trade-balance": tradeBalance,
      });
      const selectedStyle: React.CSSProperties = {
        color: "#fff",
        backgroundColor: primaryColor1,
      };
      const tradeDirectionDropdown =
        tradeDirectionDropdownIsShown === false ? null : (
          <TradeDirectionMenu ref={importExportDropdownRef}>
            <TradeDirectionMenuItem
              onClick={(e: React.MouseEvent) =>
                selectTradeDirection(e, TradeDirection.Export)
              }
              style={
                topTradeDestinationDirection === TradeDirection.Export
                  ? selectedStyle
                  : {}
              }
            >
              {getFluentString(
                "export-basket-top-trade-destination-widget-export"
              )}
            </TradeDirectionMenuItem>
            <TradeDirectionMenuItem
              onClick={(e: React.MouseEvent) =>
                selectTradeDirection(e, TradeDirection.Import)
              }
              style={
                topTradeDestinationDirection === TradeDirection.Import
                  ? selectedStyle
                  : {}
              }
            >
              {getFluentString(
                "export-basket-top-trade-destination-widget-import"
              )}
            </TradeDirectionMenuItem>
          </TradeDirectionMenu>
        );
      const tradeDirectionText =
        topTradeDestinationDirection === TradeDirection.Export
          ? getFluentString("export-basket-top-trade-destination-widget-export")
          : getFluentString(
              "export-basket-top-trade-destination-widget-import"
            );
      const topTradeDestinationArgs = {
        toggleDropdown: (
          <TradeDirectionToggleContainer>
            <TradeDirectionToggle onClick={toggleTradeDirectionDropdown}>
              {tradeDirectionText}
              <Arrow />
            </TradeDirectionToggle>
            {tradeDirectionDropdown}
          </TradeDirectionToggleContainer>
        ),
      };
      out = (
        <>
          <IntroText>{introtext}</IntroText>
          <Localized
            bundle={fluentBundle}
            messageId="export-basket-top-trade-destination-widget-text"
            messageArgs={topTradeDestinationArgs}
          >
            <TradeDirectionTitle />
          </Localized>
          <TopTradeDestinationCountriesContainer>
            {topTradeDestinationCountries}
          </TopTradeDestinationCountriesContainer>
        </>
      );
    } else {
      out = null;
    }
  } else {
    out = null;
  }

  return (
    <Slide>
      <SlideAdvancer
        onClick={onPrevSlideClick}
        direction={Direction.Previous}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString("section-nav-slide-country-pages-home")}
      </SlideAdvancer>
      <ContentContainer>
        <Title>
          {getFluentString("section-nav-slide-export-basket", {
            year: yearAsString,
          })}
        </Title>
        {out}
      </ContentContainer>
      <SlideAdvancer
        onClick={onNextSlideClick}
        direction={Direction.Next}
        isHelpActive={isHelpActive}
        helpTooltipIndex={helpTooltipIndex}
        getFluentString={getFluentString}
      >
        {getFluentString("section-nav-slide-export-complexity", {
          year: yearAsString,
        })}
      </SlideAdvancer>
    </Slide>
  );
};
